import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Edit,
  Trash2,
  Plus,
  ChevronRight,
  Clock,
  Target,
  MessageSquare,
  Ruler,
  Weight,
  TrendingUp,
  AlertTriangle,
  MapPin,
  Activity,
  Wifi,
  Home,
  HeartPulse,
  Link2,
  Copy,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { studentsAPI, plansAPI, usersAPI } from '../lib/api';
import { planWeeks, daysUntil, formatRange } from '../lib/dates';
import { LEVEL_LABELS } from '../lib/fatigue';
import { DisciplineBadge } from '../lib/disciplines';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import { StudentForm } from '../components/forms/StudentForm';
import { PlanForm } from '../components/forms/PlanForm';
import { PerformanceHistory } from '../components/PerformanceHistory';
import type { TrainingPlan, StudentFormData, PlanFormData } from '../types';
import clsx from 'clsx';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

/** Formatea la fecha de alta tolerando el formato SQL y valores inválidos. */
function memberSince(value?: string): string {
  if (!value) return '—';
  // SQLite CURRENT_TIMESTAMP devuelve 'YYYY-MM-DD HH:MM:SS' (UTC) → normalizar a ISO.
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? value.replace(' ', 'T') + 'Z'
    : value;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-AR', { year: 'numeric', month: 'long' });
}

export function StudentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [intakeUrl, setIntakeUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: student, isLoading, error } = useQuery({
    queryKey: ['student', id],
    queryFn: () => studentsAPI.getById(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<StudentFormData>) => studentsAPI.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student', id] });
      queryClient.invalidateQueries({ queryKey: ['students'] });
      setIsEditModalOpen(false);
      toast.success('Alumno actualizado');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => studentsAPI.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] });
      navigate('/students');
      toast.success('Alumno eliminado');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const createPlanMutation = useMutation({
    mutationFn: (data: PlanFormData) => plansAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student', id] });
      setIsPlanModalOpen(false);
      toast.success('Plan creado exitosamente');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const intakeLinkMutation = useMutation({
    mutationFn: () => usersAPI.createIntakeLink(id!),
    onSuccess: (res) => {
      setCopied(false);
      setIntakeUrl(`${window.location.origin}/intake/${res.token}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const copyIntakeUrl = async () => {
    if (!intakeUrl) return;
    try {
      await navigator.clipboard.writeText(intakeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="card text-center py-12">
        <h2 className="text-xl font-semibold text-dark-300 mb-2">Alumno no encontrado</h2>
        <Link to="/students" className="btn-primary mt-4">
          <ArrowLeft className="w-4 h-4" />
          Volver a Alumnos
        </Link>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Back button */}
      <motion.div variants={itemVariants}>
        <Link
          to="/students"
          className="inline-flex items-center gap-2 text-dark-400 hover:text-dark-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a Alumnos
        </Link>
      </motion.div>

      {/* Student Header */}
      <motion.div variants={itemVariants} className="card">
        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
          {/* Avatar */}
          <div className={clsx(
            'w-20 h-20 rounded-2xl flex items-center justify-center text-dark-50 font-bold text-2xl flex-shrink-0',
            student.isActive
              ? 'bg-dark-700 ring-1 ring-primary-500/30'
              : 'bg-dark-800'
          )}>
            {student.firstName[0]}{student.lastName[0]}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-display font-bold text-dark-50">
                  {student.firstName} {student.lastName}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className={clsx(
                    'badge',
                    student.isActive ? 'badge-primary' : 'badge-neutral'
                  )}>
                    {student.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className="text-dark-400 text-sm">
                    {student._count?.plans || student.plans?.length || 0} planes
                  </span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => intakeLinkMutation.mutate()}
                  disabled={intakeLinkMutation.isPending}
                  className="btn-secondary btn-sm"
                  title="Generar un link para que el alumno complete su ficha"
                >
                  {intakeLinkMutation.isPending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Link2 className="w-4 h-4" />
                  )}
                  Link de ficha
                </button>
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="btn-secondary btn-sm"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </button>
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  className="btn-danger btn-sm"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              <div className="flex items-center gap-3 text-dark-300">
                <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-dark-300" />
                </div>
                <div>
                  <p className="text-xs text-dark-500">Email</p>
                  <p className="text-sm text-dark-200">{student.email}</p>
                </div>
              </div>
              {student.phone && (
                <div className="flex items-center gap-3 text-dark-300">
                  <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-dark-400" />
                  </div>
                  <div>
                    <p className="text-xs text-dark-500">Teléfono</p>
                    <p className="text-sm text-dark-200">{student.phone}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 text-dark-300">
                <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-dark-400" />
                </div>
                <div>
                  <p className="text-xs text-dark-500">Miembro desde</p>
                  <p className="text-sm text-dark-200">{memberSince(student.createdAt)}</p>
                </div>
              </div>

              {student.trainingLevel && (
                <div className="flex items-center gap-3 text-dark-300">
                  <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-dark-300" />
                  </div>
                  <div>
                    <p className="text-xs text-dark-500">Nivel</p>
                    <p className="text-sm text-dark-200">
                      {LEVEL_LABELS[student.trainingLevel as keyof typeof LEVEL_LABELS]}
                    </p>
                  </div>
                </div>
              )}

              {student.attentionType && (
                <div className="flex items-center gap-3 text-dark-300">
                  <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                    {student.attentionType === 'remote' ? (
                      <Wifi className="w-5 h-5 text-dark-400" />
                    ) : (
                      <Home className="w-5 h-5 text-dark-300" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-dark-500">Modalidad</p>
                    <p className="text-sm text-dark-200">
                      {student.attentionType === 'remote' ? 'A distancia' : 'Presencial'}
                    </p>
                  </div>
                </div>
              )}

              {student.venue && (
                <div className="flex items-center gap-3 text-dark-300">
                  <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-dark-400" />
                  </div>
                  <div>
                    <p className="text-xs text-dark-500">Sede</p>
                    <p className="text-sm text-dark-200">{student.venue.name}</p>
                  </div>
                </div>
              )}

              {student.otherSports && (
                <div className="flex items-center gap-3 text-dark-300">
                  <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-dark-400" />
                  </div>
                  <div>
                    <p className="text-xs text-dark-500">Otros deportes</p>
                    <p className="text-sm text-dark-200">{student.otherSports}</p>
                  </div>
                </div>
              )}

              {student.heightCm != null && (
                <div className="flex items-center gap-3 text-dark-300">
                  <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                    <Ruler className="w-5 h-5 text-dark-400" />
                  </div>
                  <div>
                    <p className="text-xs text-dark-500">Altura</p>
                    <p className="text-sm text-dark-200">{student.heightCm} cm</p>
                  </div>
                </div>
              )}

              {student.weightKg != null && (
                <div className="flex items-center gap-3 text-dark-300">
                  <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                    <Weight className="w-5 h-5 text-dark-400" />
                  </div>
                  <div>
                    <p className="text-xs text-dark-500">Peso</p>
                    <p className="text-sm text-dark-200">{student.weightKg} kg</p>
                  </div>
                </div>
              )}

              {student.goal && (
                <div className="flex items-center gap-3 text-dark-300">
                  <div className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center">
                    <Target className="w-5 h-5 text-sky-500" />
                  </div>
                  <div>
                    <p className="text-xs text-dark-500">Objetivo</p>
                    <p className="text-sm text-dark-200">{student.goal}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Salud / lesiones */}
            {student.healthNotes && (
              <div className="mt-6 p-4 rounded-xl bg-dark-800/50 border border-primary-500/20">
                <p className="text-sm text-primary-300 mb-1 flex items-center gap-1.5">
                  <HeartPulse className="w-4 h-4" />
                  Lesiones / condiciones de salud
                </p>
                <p className="text-dark-200 whitespace-pre-wrap">{student.healthNotes}</p>
              </div>
            )}

            {/* Notes */}
            {student.notes && (
              <div className="mt-4 p-4 rounded-xl bg-dark-800/50 border border-dark-700/50">
                <p className="text-sm text-dark-400 mb-1">Notas</p>
                <p className="text-dark-200">{student.notes}</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Plans Section */}
      <motion.div variants={itemVariants} className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-dark-50 flex items-center gap-2">
            <Target className="w-5 h-5 text-dark-300" />
            Planes de Entrenamiento
          </h2>
          <button
            onClick={() => setIsPlanModalOpen(true)}
            className="btn-primary btn-sm"
          >
            <Plus className="w-4 h-4" />
            Nuevo Plan
          </button>
        </div>

        {student.plans && student.plans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {student.plans.map((plan: TrainingPlan) => (
              <Link
                key={plan.id}
                to={`/plans/${plan.id}`}
                className="card-hover group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-dark-100 group-hover:text-primary-400 transition-colors">
                        {plan.name}
                      </h3>
                      <span className={clsx(
                        'badge',
                        plan.isActive ? 'badge-primary' : 'badge-neutral'
                      )}>
                        {plan.isActive ? 'Activo' : 'Finalizado'}
                      </span>
                      <DisciplineBadge discipline={plan.discipline} />
                    </div>
                    {plan.description && (
                      <p className="text-sm text-dark-400 mt-1 line-clamp-2">
                        {plan.description}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-dark-500 group-hover:text-primary-400 transition-colors" />
                </div>

                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-dark-700/50 flex-wrap text-sm text-dark-400">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatRange(plan.startDate, plan.endDate)}
                  </div>
                  {plan.endDate && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {planWeeks(plan.startDate, plan.endDate)} sem
                    </div>
                  )}
                  {plan.isActive && plan.endDate && (() => {
                    const d = daysUntil(plan.endDate);
                    if (d > 7) return null;
                    return (
                      <span className={clsx(
                        'flex items-center gap-1 font-medium',
                        d < 0 ? 'text-red-400' : 'text-amber-400'
                      )}>
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {d < 0 ? 'Vencido' : d === 0 ? 'Vence hoy' : `${d} día${d !== 1 ? 's' : ''}`}
                      </span>
                    );
                  })()}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <Calendar className="w-16 h-16 mx-auto text-dark-600 mb-4" />
            <h3 className="text-lg font-semibold text-dark-300 mb-2">Sin planes de entrenamiento</h3>
            <p className="text-dark-400 mb-6">
              Crea el primer plan de entrenamiento para este alumno
            </p>
            <button onClick={() => setIsPlanModalOpen(true)} className="btn-primary">
              <Plus className="w-5 h-5" />
              Crear Plan
            </button>
          </div>
        )}
      </motion.div>

      {/* Rendimiento registrado (progresión por ejercicio) */}
      <motion.div variants={itemVariants}>
        <PerformanceHistory studentId={student.id} />
      </motion.div>

      {/* Recent Comments */}
      {student.comments && student.comments.length > 0 && (
        <motion.div variants={itemVariants} className="card">
          <h2 className="text-lg font-semibold text-dark-50 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-dark-400" />
            Comentarios Recientes
          </h2>
          <div className="space-y-3">
            {student.comments.slice(0, 5).map((comment: any) => (
              <div key={comment.id} className="p-3 rounded-xl bg-dark-800/50">
                <p className="text-dark-200 text-sm">{comment.content}</p>
                <p className="text-xs text-dark-500 mt-2">
                  {new Date(comment.createdAt).toLocaleDateString('es-AR')}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar Alumno"
      >
        <StudentForm
          initialData={student}
          onSubmit={(data) => updateMutation.mutate(data)}
          onCancel={() => setIsEditModalOpen(false)}
          isLoading={updateMutation.isPending}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Eliminar Alumno"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            ¿Estás seguro de que deseas eliminar a{' '}
            <span className="font-semibold text-dark-100">
              {student.firstName} {student.lastName}
            </span>
            ? Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setIsDeleteModalOpen(false)} className="btn-secondary">
              Cancelar
            </button>
            <button
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="btn-danger"
            >
              {deleteMutation.isPending ? <LoadingSpinner size="sm" /> : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* New Plan Modal */}
      <Modal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
        title="Nuevo Plan de Entrenamiento"
        size="lg"
      >
        <PlanForm
          studentId={student.id}
          onSubmit={(data) => createPlanMutation.mutate(data)}
          onCancel={() => setIsPlanModalOpen(false)}
          isLoading={createPlanMutation.isPending}
        />
      </Modal>

      {/* Link de ficha de alta */}
      <Modal isOpen={!!intakeUrl} onClose={() => setIntakeUrl(null)} title="Link de ficha de alta">
        <div className="space-y-4">
          <p className="text-dark-300 text-sm">
            Compartí este link con {student.firstName} (por WhatsApp, por ejemplo) para que
            complete sus datos. <span className="text-dark-400">Vence en 14 días</span> y podés
            regenerarlo cuando quieras (invalida el anterior).
          </p>
          <div className="flex gap-2">
            <input readOnly value={intakeUrl ?? ''} className="input flex-1 text-sm" onFocus={(e) => e.target.select()} />
            <button onClick={copyIntakeUrl} className="btn-primary !px-3" title="Copiar">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-dark-500">
            El atleta completa nombre, contacto, datos físicos, objetivo, otros deportes y
            lesiones/salud. Vos seguís controlando nivel, modalidad y sede.
          </p>
        </div>
      </Modal>
    </motion.div>
  );
}

